/* eslint-disable @typescript-eslint/no-unused-vars */
import { IntegrationAppProvider, useIntegrationApp } from "@membranehq/react";
import { useMemo, useState, useEffect } from "react";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import { AlertTriangle, Check, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import GoogleCalendarNewFlow from "./new-flow/GoogleCalendarNewFlow";
import {
    queryClient,
    useAIMemoryObjects,
    useCreateSyncConfiguration,
    useCurrentUser,
    useGoogleCalendarIntegration,
    useMemberIntegrationsMe,
    useSyncConfigurations,
    useUpdateMemberIntegration,
    useUpdateSyncConfiguration,
    useWorkspaceMembers
} from "../../../hooks/QueryHooks";
import { useQuery } from "@tanstack/react-query";
import { getProvider } from "../../../integrations/providers";
import { toast } from "sonner";
import { generateIntegrationAppToken } from "../../../services/integrationAppEndpoint";
import { triggerBestEffortInitialSync, notifyBackendOfConnection } from "../../../integrations/integrationAppPostConnect";
import { activateInboundSyncForProvider } from "../../../integrations/activateInboundSync";
import { updateMemberIntegration, createMemberIntegration, getMemberIntegrationsMe } from "../../../services/memberIntegrationsEndpoint";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../components/ui/accordion";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import IntegrationDetailShell from "../../home/settings/integrations/components/IntegrationDetailShell";
import { cn } from "../../../lib/utils";

interface GoogleCalendarPageContentProps {
    isConnected: boolean;
    connectedAt: string | null;
    integrationLoading: boolean;
    integrationId: string;
    onConnect: () => void;
    connectionData: any;
    setConnectionData: (data: any) => void;
    setIsConnected: (connected: boolean) => void;
    setConnectedAt: (timestamp: string | null) => void;
    setIntegrationLoading: (loading: boolean) => void;
}

const normalizeProvider = (provider: string | undefined) => (provider || '').toLowerCase().replace(/_/g, '-');

type CalendarSettingRowProps = {
    label: string;
    description?: string;
    children: React.ReactNode;
};

const CalendarSettingRow = ({ label, description, children }: CalendarSettingRowProps) => (
    <div className="flex items-start justify-between gap-6 py-3">
        <div className="min-w-0">
            <p className="text-[13px] text-foreground">{label}</p>
            {description ? <p className="mt-0.5 text-[11px] text-muted-foreground">{description}</p> : null}
        </div>
        <div className="shrink-0">{children}</div>
    </div>
);

const GoogleCalendarMemberSettings = ({ isConnected }: { isConnected: boolean }) => {
    const workspace: any = useWorkspaceStore((state) => state.workspace);
    const updateMemberIntegrationMutation = useUpdateMemberIntegration();

    const {
        data: memberIntegrations,
        isFetching: memberIntegrationsFetching,
        refetch: refetchMemberIntegrations,
    }: any = useMemberIntegrationsMe({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id,
    });

    const calendarMemberIntegrations = useMemo(() => (memberIntegrations || [])
        .filter((mi: any) => normalizeProvider(mi?.provider) === "google-calendar"), [memberIntegrations]);

    const [selectedMemberIntegrationId, setSelectedMemberIntegrationId] = useState<string | null>(null);
    const selectedMemberIntegration = useMemo(() => (
        calendarMemberIntegrations.find((mi: any) => mi?.id === selectedMemberIntegrationId)
        || calendarMemberIntegrations.find((mi: any) => mi?.is_default_for_member)
        || calendarMemberIntegrations[0]
        || null
    ), [calendarMemberIntegrations, selectedMemberIntegrationId]);

    useEffect(() => {
        if (!calendarMemberIntegrations?.length) {
            setSelectedMemberIntegrationId(null);
            return;
        }
        const nextDefaultId = (
            calendarMemberIntegrations.find((mi: any) => mi?.id === selectedMemberIntegrationId)?.id
            || calendarMemberIntegrations.find((mi: any) => mi?.is_default_for_member)?.id
            || calendarMemberIntegrations[0]?.id
        );
        if (nextDefaultId && nextDefaultId !== selectedMemberIntegrationId) {
            setSelectedMemberIntegrationId(nextDefaultId);
        }
    }, [calendarMemberIntegrations, selectedMemberIntegrationId]);

    const [isDefaultForMember, setIsDefaultForMember] = useState<boolean>(false);
    const [defaultVisibility, setDefaultVisibility] = useState<"default" | "private" | "public">("default");
    const [syncWindowDaysBack, setSyncWindowDaysBack] = useState<number | ''>(30);
    const [syncWindowDaysForward, setSyncWindowDaysForward] = useState<number | ''>(30);
    const [includeDeclinedEvents, setIncludeDeclinedEvents] = useState<boolean>(false);
    const [createEventsAsOrganizer, setCreateEventsAsOrganizer] = useState<boolean>(true);
    const [roomsCalendarIdsInput, setRoomsCalendarIdsInput] = useState<string>("");
    const [excludeInternalEvents, setExcludeInternalEvents] = useState<boolean>(true);
    const [popupReminderMinutes, setPopupReminderMinutes] = useState<number | ''>('');
    const [emailReminderMinutes, setEmailReminderMinutes] = useState<number | ''>('');

    const [activeTab, setActiveTab] = useState<"general" | "sync" | "rooms">("general");

    useEffect(() => {
        if (!selectedMemberIntegration) return;

        const options = selectedMemberIntegration?.options || {};
        const reminders = (options?.default_reminders || options?.defaultReminders || []) as any[];
        const popupReminder = reminders.find((r) => (r?.method || '').toLowerCase() === 'popup');
        const emailReminder = reminders.find((r) => (r?.method || '').toLowerCase() === 'email');
        const defaultVisibilityOption = options?.default_visibility ?? options?.defaultVisibility;
        const resolvedDefaultVisibility = (
            defaultVisibilityOption === "private" || defaultVisibilityOption === "public"
                ? defaultVisibilityOption
                : "default"
        );

        setIsDefaultForMember(!!selectedMemberIntegration?.is_default_for_member);
        setDefaultVisibility(resolvedDefaultVisibility);
        setSyncWindowDaysBack(options?.sync_window_days_back ?? 30);
        setSyncWindowDaysForward(options?.sync_window_days_forward ?? 30);
        setIncludeDeclinedEvents(!!(options?.include_declined_events ?? false));
        setCreateEventsAsOrganizer(!!(options?.create_events_as_organizer ?? true));
        setRoomsCalendarIdsInput((options?.rooms_calendar_ids || []).join(", "));
        setExcludeInternalEvents(!!(options?.exclude_internal_events ?? true));
        setPopupReminderMinutes(popupReminder?.minutes ?? '');
        setEmailReminderMinutes(emailReminder?.minutes ?? '');
    }, [selectedMemberIntegration?.id]);

    useEffect(() => {
        if (isConnected) {
            refetchMemberIntegrations?.();
        }
    }, [isConnected, refetchMemberIntegrations]);

    const parseCsv = (value: string) => value.split(",").map((part) => part.trim()).filter(Boolean);

    const saveSettings = async () => {
        if (!workspace?.id || !selectedMemberIntegration?.id) return;

        const existingOptions = selectedMemberIntegration?.options || {};
        const defaultReminders: Array<{ method: "email" | "popup"; minutes: number }> = [];
        if (popupReminderMinutes !== '') defaultReminders.push({ method: "popup", minutes: Number(popupReminderMinutes) });
        if (emailReminderMinutes !== '') defaultReminders.push({ method: "email", minutes: Number(emailReminderMinutes) });

        const nextOptions: Record<string, any> = {
            ...existingOptions,
            default_visibility: defaultVisibility,
            default_reminders: defaultReminders,
            sync_window_days_back: syncWindowDaysBack === '' ? null : Number(syncWindowDaysBack),
            sync_window_days_forward: syncWindowDaysForward === '' ? null : Number(syncWindowDaysForward),
            include_declined_events: includeDeclinedEvents,
            create_events_as_organizer: createEventsAsOrganizer,
            rooms_calendar_ids: parseCsv(roomsCalendarIdsInput),
            exclude_internal_events: excludeInternalEvents,
        };

        try {
            await updateMemberIntegrationMutation.mutateAsync({
                workspace_id: workspace.id,
                id: selectedMemberIntegration.id,
                is_default_for_member: isDefaultForMember,
                options: nextOptions,
            });
            await refetchMemberIntegrations?.();
            toast.success("Saved", { description: "Calendar settings updated." });
        } catch (e) {
            toast.error("Save failed", { description: "Could not update calendar settings." });
        }
    };

    return (
        <div className="rounded-lg border border-border">
            <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-4">
                <div>
                    <h3 className="text-[14px] font-semibold text-foreground">Advanced Google Calendar settings</h3>
                    <p className="mt-0.5 text-[12px] text-muted-foreground">
                        Optional controls for reminders, sync rules, and rooms. Most teams can leave defaults.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-[12px]"
                    onClick={() => refetchMemberIntegrations?.()}
                    disabled={memberIntegrationsFetching}
                >
                    <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${memberIntegrationsFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            <div className="space-y-4 px-4 py-4">
                {!isConnected ? (
                    <Alert className="border-border bg-muted/40">
                        <AlertDescription className="text-[13px] text-muted-foreground">
                            Connect Google Calendar to configure per-account meeting settings.
                        </AlertDescription>
                    </Alert>
                ) : null}

                {isConnected && calendarMemberIntegrations.length === 0 ? (
                    <Alert className="border-status-cancelled/20 bg-status-cancelled/5">
                        <AlertDescription className="text-[13px] text-muted-foreground">
                            No Google Calendar accounts found yet. If you just connected, click Refresh.
                        </AlertDescription>
                    </Alert>
                ) : null}

                {calendarMemberIntegrations.length > 0 && selectedMemberIntegration ? (
                    <>
                        <div className="space-y-1 divide-y divide-border rounded-lg border border-border px-4">
                            {calendarMemberIntegrations.length > 1 ? (
                                <div className="space-y-1.5 py-3">
                                    <Label className="text-[12px] text-muted-foreground">Connected account</Label>
                                    <Select
                                        value={selectedMemberIntegration?.id || ""}
                                        onValueChange={(value) => setSelectedMemberIntegrationId(value)}
                                    >
                                        <SelectTrigger className="h-8 text-[13px]">
                                            <SelectValue placeholder="Select calendar account" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {calendarMemberIntegrations.map((integration: any) => (
                                                <SelectItem key={integration.id} value={integration.id}>
                                                    {integration.primary_email || integration.external_account_id || integration.id}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            ) : null}

                            <CalendarSettingRow
                                label="Default calendar account"
                                description="Use this account as your default calendar integration."
                            >
                                <Switch
                                    checked={isDefaultForMember}
                                    onCheckedChange={setIsDefaultForMember}
                                />
                            </CalendarSettingRow>
                        </div>

                        <Accordion type="single" collapsible className="rounded-lg border border-border px-4">
                            <AccordionItem value="advanced-calendar-controls" className="border-b-0">
                                <AccordionTrigger className="py-3 text-[13px] font-medium text-foreground hover:no-underline">
                                    Advanced sync and reminder controls
                                </AccordionTrigger>
                                <AccordionContent className="space-y-4 pb-3">
                                    <div className="flex gap-1 border-b border-border">
                                        {[
                                            { key: "general", label: "General" },
                                            { key: "sync", label: "Sync Rules" },
                                            { key: "rooms", label: "Rooms" },
                                        ].map((tab) => (
                                            <button
                                                key={tab.key}
                                                type="button"
                                                onClick={() => setActiveTab(tab.key as "general" | "sync" | "rooms")}
                                                className={cn(
                                                    "px-2.5 py-1.5 text-[12px] font-medium transition-colors border-b-2 -mb-px",
                                                    activeTab === tab.key
                                                        ? "border-primary text-foreground"
                                                        : "border-transparent text-muted-foreground hover:text-foreground",
                                                )}
                                            >
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>

                                    {activeTab === "general" ? (
                                        <div className="space-y-1 divide-y divide-border rounded-lg border border-border px-4">
                                            <div className="space-y-1.5 py-3">
                                                <Label className="text-[12px] text-muted-foreground">Default visibility</Label>
                                                <Select value={defaultVisibility} onValueChange={(v) => setDefaultVisibility((v as "default" | "private" | "public") || "default")}>
                                                    <SelectTrigger className="h-8 text-[13px]">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="default">Default</SelectItem>
                                                        <SelectItem value="private">Private</SelectItem>
                                                        <SelectItem value="public">Public</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            <CalendarSettingRow
                                                label="Create events as organizer"
                                                description="Set yourself as organizer when creating events."
                                            >
                                                <Switch
                                                    checked={createEventsAsOrganizer}
                                                    onCheckedChange={setCreateEventsAsOrganizer}
                                                />
                                            </CalendarSettingRow>

                                            <div className="grid gap-3 py-3 md:grid-cols-2">
                                                <div className="space-y-1.5">
                                                    <Label className="text-[12px] text-muted-foreground">Popup reminder (minutes)</Label>
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        max={1440}
                                                        value={popupReminderMinutes}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setPopupReminderMinutes(value === "" ? "" : Number(value));
                                                        }}
                                                        className="h-8 text-[13px]"
                                                    />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <Label className="text-[12px] text-muted-foreground">Email reminder (minutes)</Label>
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        max={1440}
                                                        value={emailReminderMinutes}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setEmailReminderMinutes(value === "" ? "" : Number(value));
                                                        }}
                                                        className="h-8 text-[13px]"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}

                                    {activeTab === "sync" ? (
                                        <div className="space-y-1 divide-y divide-border rounded-lg border border-border px-4">
                                            <div className="space-y-3 py-3">
                                                <p className="text-[13px] text-foreground">Sync window</p>
                                                <p className="text-[11px] text-muted-foreground">
                                                    Limits how far back/forward we sync meetings to reduce load.
                                                </p>
                                                <div className="grid gap-3 sm:grid-cols-2">
                                                    <div className="space-y-1.5">
                                                        <Label className="text-[12px] text-muted-foreground">Days back</Label>
                                                        <Input
                                                            type="number"
                                                            min={1}
                                                            max={365}
                                                            value={syncWindowDaysBack}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                setSyncWindowDaysBack(value === "" ? "" : Number(value));
                                                            }}
                                                            className="h-8 text-[13px]"
                                                        />
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <Label className="text-[12px] text-muted-foreground">Days forward</Label>
                                                        <Input
                                                            type="number"
                                                            min={1}
                                                            max={365}
                                                            value={syncWindowDaysForward}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                setSyncWindowDaysForward(value === "" ? "" : Number(value));
                                                            }}
                                                            className="h-8 text-[13px]"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <CalendarSettingRow
                                                label="Include declined events"
                                                description="Sync events you have declined."
                                            >
                                                <Switch
                                                    checked={includeDeclinedEvents}
                                                    onCheckedChange={setIncludeDeclinedEvents}
                                                />
                                            </CalendarSettingRow>

                                            <CalendarSettingRow
                                                label="Exclude internal-only events"
                                                description="Skip events that only include your organization's members."
                                            >
                                                <Switch
                                                    checked={excludeInternalEvents}
                                                    onCheckedChange={setExcludeInternalEvents}
                                                />
                                            </CalendarSettingRow>
                                        </div>
                                    ) : null}

                                    {activeTab === "rooms" ? (
                                        <div className="space-y-1 divide-y divide-border rounded-lg border border-border px-4">
                                            <div className="space-y-1.5 py-3">
                                                <Label className="text-[12px] text-muted-foreground">Room calendar IDs</Label>
                                                <Input
                                                    value={roomsCalendarIdsInput}
                                                    onChange={(e) => setRoomsCalendarIdsInput(e.target.value)}
                                                    placeholder="room_1@resource.calendar.google.com, room_2@resource.calendar.google.com"
                                                    className="h-8 text-[13px]"
                                                />
                                                <p className="text-[11px] text-muted-foreground">
                                                    Comma-separated list of room calendar IDs to include in sync.
                                                </p>
                                            </div>
                                        </div>
                                    ) : null}

                                    <div className="flex justify-end">
                                        <Button
                                            type="button"
                                            size="sm"
                                            className="h-7 text-[12px]"
                                            onClick={() => { saveSettings(); }}
                                            disabled={!selectedMemberIntegration?.id || updateMemberIntegrationMutation.isLoading}
                                        >
                                            {updateMemberIntegrationMutation.isLoading ? "Saving..." : "Save Settings"}
                                        </Button>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </>
                ) : null}
            </div>
        </div>
    );
};

// Member-level IntegrationApp provider for Google Calendar
const MemberLevelIntegrationProvider = ({ children }: { children: React.ReactNode }) => {
    const workspace: any = useWorkspaceStore((state) => state.workspace);

    const fetchMemberLevelToken = async () => {
        if (workspace?.id) {
            try {
                const response = await generateIntegrationAppToken({
                    workspace_id: workspace.id,
                    isMemberLevel: true
                });
                console.log('response', response);
                return response?.data?.token;
            } catch (error) {
                console.error('Failed to fetch member-level integration token:', error);
                return null;
            }
        }
        return null;
    };

    return (
        <IntegrationAppProvider fetchToken={fetchMemberLevelToken}>
            {children}
        </IntegrationAppProvider>
    );
};

// Component that uses member-level IntegrationApp context
const GoogleCalendarPageContent = ({
    isConnected,
    connectedAt,
    integrationLoading,
    integrationId,
    onConnect,
    connectionData,
    setConnectionData,
    setIsConnected,
    setConnectedAt,
    setIntegrationLoading
}: GoogleCalendarPageContentProps) => {
    const workspace: any = useWorkspaceStore((state) => state.workspace);
    const provider = getProvider('google-calendar');
    const integrationApp = useIntegrationApp();
    const navigate = useNavigate();

    // Sync-configuration management used to fully activate the inbound sync on connect
    const createSyncConfigMutation = useCreateSyncConfiguration();
    const updateSyncConfigMutation = useUpdateSyncConfiguration();
    const { refetch: refetchSyncConfigurations } = useSyncConfigurations({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id,
    } as any);

    const {
        data: memberIntegrations,
    }: any = useMemberIntegrationsMe({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id,
    });

    const calendarMemberIntegrations = useMemo(() => (memberIntegrations || [])
        .filter((mi: any) => normalizeProvider(mi?.provider) === "google-calendar"), [memberIntegrations]);

    const { data: workspaceMembersData }: any = useWorkspaceMembers({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id,
    });
    const { data: user }: any = useCurrentUser({});

    const currentWorkspaceMember = useMemo(() => {
        if (!user?.email) return null;
        const members = workspaceMembersData?.members || workspaceMembersData?.users || [];
        const needle = String(user.email).toLowerCase().trim();
        return members.find((m: any) => String(m?.email || "").toLowerCase().trim() === needle) || null;
    }, [workspaceMembersData, user?.email]);

    const selectedMemberIntegration = useMemo(() => (
        calendarMemberIntegrations.find((mi: any) => mi?.is_default_for_member)
        || calendarMemberIntegrations[0]
        || null
    ), [calendarMemberIntegrations]);

    const connectedEmail = selectedMemberIntegration?.primary_email || selectedMemberIntegration?.external_account_id;
    const connectedDateLabel = connectedAt
        ? new Date(connectedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : null;
    const [expandedObjects, setExpandedObjects] = useState<Record<string, boolean>>({});

    const { data: aiMemoryData, refetch: refetchAIMemory }: any = useAIMemoryObjects({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id && isConnected,
        staleTime: 0,
        refetchOnMount: "always",
        refetchInterval: isConnected ? 5000 : false,
    } as any);

    const aiMemoryMeetingsObject = useMemo(() => {
        if (!Array.isArray(aiMemoryData?.data)) return null;
        return aiMemoryData.data.find((item: any) => item?.api_slug === "meetings") || null;
    }, [aiMemoryData]);

    const { data: meetingsFlowEnabled = false, isFetching: checkingMeetingsFlowStatus } = useQuery({
        queryKey: ["google-calendar-meetings-flow-status", workspace?.id, integrationId, provider.key],
        enabled: !!workspace?.id && !!integrationId && isConnected,
        staleTime: 0,
        refetchOnMount: "always",
        refetchInterval: isConnected ? 5000 : false,
        queryFn: async () => {
            const [firstFlowResult, receiveFlowResult] = await Promise.allSettled([
                integrationApp.connection(provider.key).flow("first-sync-for-meetings").get(),
                integrationApp.connection(provider.key).flow("receive-meeting-events").get(),
            ]);

            const firstEnabled = firstFlowResult.status === "fulfilled" && !!firstFlowResult.value?.enabled;
            const receiveEnabled = receiveFlowResult.status === "fulfilled" && !!receiveFlowResult.value?.enabled;
            return firstEnabled || receiveEnabled;
        },
    });

    const syncRow = useMemo(() => {
        const aiEnabled = aiMemoryMeetingsObject?.status === "enabled";
        const enabled = aiEnabled && meetingsFlowEnabled;
        const fieldCount = aiMemoryMeetingsObject?.field_count || 0;
        return {
            key: "meetings",
            label: "Meetings",
            enabled,
            configured: aiEnabled,
            errors: 0,
            mappedFields: fieldCount,
            lastSyncLabel: connectedAt
                ? new Date(connectedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                : null,
        };
    }, [aiMemoryMeetingsObject, meetingsFlowEnabled, connectedAt]);

    const toggleExpand = (key: string) => {
        setExpandedObjects((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    // Check for existing connection on component mount
    const [isCheckingConnection, setIsCheckingConnection] = useState<boolean>(true);

    useEffect(() => {
        const checkConnection = async () => {
            try {
                const integration = await integrationApp.integration(provider.key).get();
                const existingConnection = integration?.connection;
                if (existingConnection && (!existingConnection.disconnected)) {
                    setConnectionData(integration);
                    setIsConnected(true);
                    // Best-effort connection timestamp
                    const ts = (existingConnection as any)?.connectedAt || (integration as any)?.createdAt || (integration as any)?.updatedAt || new Date().toISOString();
                    setConnectedAt(ts);
                }
            } catch (error) {
                // no existing connection
            } finally {
                setIsCheckingConnection(false);
            }
        };

        checkConnection();
    }, [integrationApp, provider.key, setConnectionData, setIsConnected, setConnectedAt]);

    const googleCalendarClick = async () => {
        setIntegrationLoading(true);
        const ensureInfra = async () => {
            try {
                await Promise.allSettled([
                    integrationApp.connection(provider.key).dataSource('meetings').get({ autoCreate: true }),
                ]);

                await Promise.allSettled([
                    integrationApp.connection(provider.key).fieldMapping('meetings').get({ autoCreate: true }),
                ]);
            } catch (e) {
                // Best-effort provisioning; log but do not block UX
                console.error('Failed to ensure data sources/field mappings for connection', provider.key, e);
            }
        };

        const updateMemberIntegrationWithEmail = async () => {
            try {
                // Get email using custom action
                const getEmailResponse = await integrationApp.connection(provider.key).action('get-user-email').run();
                let extractedEmail = (getEmailResponse as any)?.output?.data?.id;

                // If get-user-email yields nothing, fall back to an identifier from the
                // connection itself. We must NOT skip backend creation in that case —
                // the canonical upsert below still needs to run.
                let fallbackEmail = '';
                if (!extractedEmail) {
                    try {
                        const connectionObj: any = await integrationApp.connection(provider.key).get();
                        fallbackEmail =
                            connectionObj?.primary_email ||
                            connectionObj?.externalAccountId ||
                            connectionObj?.accountData?.email ||
                            connectionObj?.accountData?.emailAddress ||
                            connectionObj?.accountData?.primaryEmail ||
                            connectionObj?.name ||
                            '';
                    } catch (e) {
                        // best-effort only — fall through to the synthesized identifier below
                    }
                    console.warn('Could not get email from Google Calendar integration; using fallback', fallbackEmail);
                }

                // Best available identifier for the backend upsert. Never empty so the
                // member-integration row is created regardless of get-user-email result.
                const resolvedEmail = extractedEmail || fallbackEmail || `google-calendar@${provider.key}`;

                if (workspace?.id) {
                    // Local create/update path (kept for backwards compatibility). Only
                    // attempt the local upsert when we actually resolved a real email.
                    if (extractedEmail || fallbackEmail) {
                        const localEmail = extractedEmail || fallbackEmail;
                        // Get member integrations to find the one to update
                        const memberIntegrationsResponse = await getMemberIntegrationsMe({ workspace_id: workspace.id });
                        const fetchedMemberIntegrations = memberIntegrationsResponse?.data || [];

                        const calendarMemberIntegration = fetchedMemberIntegrations.find((mi: any) =>
                            normalizeProvider(mi?.provider) === "google-calendar" &&
                            (mi?.is_default_for_member || mi?.primary_email === null || mi?.external_account_id === null)
                        );

                        if (calendarMemberIntegration && calendarMemberIntegration.id) {
                            await updateMemberIntegration({
                                workspace_id: workspace.id,
                                id: calendarMemberIntegration.id,
                                primary_email: localEmail,
                                external_account_id: localEmail, // We use email as external_id if missing
                            });
                            console.log('Updated Google Calendar member integration with email:', localEmail);
                        } else if (currentWorkspaceMember?.id && integrationId) {
                            // Create new member integration if existing one not found or not matched
                            // We need current workspace member ID
                            await createMemberIntegration({
                                workspace_id: workspace.id,
                                workspace_member_id: currentWorkspaceMember.id,
                                integration_id: integrationId,
                                provider: "google-calendar",
                                external_account_id: localEmail,
                                primary_email: localEmail,
                                is_active: true,
                                is_default_for_member: true,
                                options: {},
                            });
                            console.log('Created Google Calendar member integration with email:', localEmail);
                        } else {
                            console.warn('Cannot create member integration locally: missing workspace member ID or integration ID', {
                                memberId: currentWorkspaceMember?.id,
                                integrationId
                            });
                        }
                    }

                    // Canonical backend upsert — resolves the member from auth, auto-creates
                    // the parent Integrations row + WorkspaceMemberIntegration row, and enables
                    // the provider's AI-memory objects. Runs regardless of the local branch.
                    await notifyBackendOfConnection(workspace.id, 'google-calendar', provider.key, resolvedEmail, resolvedEmail);
                }

                // Invalidate member integrations cache to refresh UI
                queryClient.invalidateQueries(["member-integrations"]);
            } catch (error) {
                console.error('Failed to update/create member integration with email:', error);
            }
        };

        // Fully activate the inbound (meetings) sync on connect — mirrors the manual
        // "Start sync" recipe so the user does not have to enable it by hand.
        // Falls back to a best-effort flow trigger if prerequisites can't be resolved.
        const activateMeetingsSync = async () => {
            const slug = 'meetings';
            try {
                // The backend auto-enables the AI-memory objects on connect; fetch them
                // fresh to obtain the object_definition id before activating.
                let destinationObjectId: string | undefined;
                try {
                    const refreshed: any = await refetchAIMemory?.();
                    const aiObjects = refreshed?.data?.data || [];
                    destinationObjectId = aiObjects.find((o: any) => o?.api_slug === slug)?.object_id;
                } catch (e) {
                    // best-effort
                }

                // Resolve the parent integration_id (the member integration's integration_id).
                let resolvedIntegrationId: string | undefined = integrationId;
                if (!resolvedIntegrationId && workspace?.id) {
                    try {
                        const resp: any = await getMemberIntegrationsMe({ workspace_id: workspace.id });
                        const list = resp?.data || [];
                        resolvedIntegrationId = list.find((mi: any) => normalizeProvider(mi?.provider) === 'google-calendar')?.integration_id;
                    } catch (e) {
                        // best-effort
                    }
                }

                // Fresh existing sync configs (so we reuse rather than duplicate).
                let existingSyncConfigs: any = [];
                try {
                    const refreshed: any = await refetchSyncConfigurations?.();
                    existingSyncConfigs = refreshed?.data ?? [];
                } catch (e) {
                    // best-effort
                }

                const activated = await activateInboundSyncForProvider({
                    integrationApp,
                    workspaceId: workspace?.id,
                    integrationId: resolvedIntegrationId,
                    providerKey: provider.key,
                    slug,
                    destinationObjectId,
                    sourceObject: provider.defaultSourceCollections?.meetings,
                    createSyncConfig: (args) => createSyncConfigMutation.mutateAsync(args),
                    updateSyncConfig: (args) => updateSyncConfigMutation.mutateAsync(args),
                    existingSyncConfigs,
                });

                if (!activated) {
                    // Could not resolve sync-config/object id — fall back to best-effort.
                    await triggerBestEffortInitialSync(integrationApp, provider.key);
                }
            } catch (e) {
                await triggerBestEffortInitialSync(integrationApp, provider.key);
            }
        };

        if (isConnected) {
            try {
                const connection = await integrationApp.integration(provider.key).openNewConnection();
                if (connection && (connection as any).state === 'READY') {
                    setConnectionData(connection);
                    setIsConnected(true);
                    setConnectedAt(new Date().toISOString());
                    // Ensure data sources and field mappings exist
                    await ensureInfra();
                    // Update member integration with connected email
                    await updateMemberIntegrationWithEmail();
                    // Fully activate the inbound meetings sync (with best-effort fallback)
                    await activateMeetingsSync();
                }
            } catch (error) {
                console.error("Connection failed:", error);
            }
        } else {
            try {
                const connection = await integrationApp.integration(provider.key).connect();
                if (connection && (connection as any).state === 'READY') {
                    setConnectionData(connection);
                    setIsConnected(true);
                    setConnectedAt(new Date().toISOString());
                    // Run on first connect
                    await ensureInfra();
                    // Update member integration with connected email
                    await updateMemberIntegrationWithEmail();
                    // Fully activate the inbound meetings sync (with best-effort fallback)
                    await activateMeetingsSync();
                }
            } catch (error) {
                console.error("Connection failed:", error);
            }
        }

        setIntegrationLoading(false);
    };

    const handleBack = () => {
        if (!workspace?.unique_identifier) {
            navigate(-1);
            return;
        }

        navigate(`/home/${workspace.unique_identifier}/settings/integrations`);
    };

    return (
        <IntegrationDetailShell
            integrationName={provider.label}
            connected={isConnected}
            credentialOk={isConnected}
            externalId={connectedEmail || connectionData?.id || integrationId || null}
            lastSync={connectedDateLabel}
            reconnectLoading={integrationLoading || isCheckingConnection}
            reconnectLabel={isConnected ? "Reconnect" : "Connect"}
            onReconnect={googleCalendarClick}
            onBack={handleBack}
        >
            {!isConnected ? (
                <div className="rounded-lg border border-border p-4">
                    <p className="text-[13px] text-foreground">
                        Connect Google Calendar to start syncing your meetings.
                    </p>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        Once connected, configure and sync your calendar objects below.
                    </p>
                </div>
            ) : null}

            {isCheckingConnection ? (
                <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
                    Checking connection...
                </div>
            ) : (
                <>
                    <div className="mb-4">
                        <h2 className="mb-3 text-[13px] font-medium text-foreground">Sync Objects</h2>
                        <div className="rounded-lg border border-border divide-y divide-border">
                            <div key={syncRow.key}>
                                <button
                                    type="button"
                                    onClick={() => toggleExpand(syncRow.key)}
                                    className="flex w-full items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        {expandedObjects[syncRow.key] ? (
                                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                        ) : (
                                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                        )}
                                        <span className="text-[13px] font-medium text-foreground">{syncRow.label}</span>
                                        {!syncRow.enabled && (
                                            <span className="text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Disabled</span>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                                        {syncRow.enabled ? (
                                            <div className="flex items-center gap-1 text-status-active">
                                                <Check className="h-3 w-3" />
                                                <span>Healthy</span>
                                            </div>
                                        ) : checkingMeetingsFlowStatus ? (
                                            <div className="flex items-center gap-1">
                                                <span>Checking sync</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1 text-status-cancelled">
                                                <AlertTriangle className="h-3 w-3" />
                                                <span>Not enabled</span>
                                            </div>
                                        )}
                                        <span>{syncRow.mappedFields} fields</span>
                                        {syncRow.lastSyncLabel ? <span>Synced {syncRow.lastSyncLabel}</span> : null}
                                    </div>
                                </button>

                                {expandedObjects[syncRow.key] ? (
                                    <div className="px-4 pb-3 ml-7">
                                        <GoogleCalendarNewFlow
                                            isConnected={isConnected}
                                            integrationId={integrationId}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <GoogleCalendarMemberSettings isConnected={isConnected} />
                </>
            )}
        </IntegrationDetailShell>
    );
};

const GoogleCalendarPage = () => {
    const workspace: any = useWorkspaceStore((state) => state.workspace);
    const [integrationLoading, setIntegrationLoading] = useState<boolean>(false);
    const [connectionData, setConnectionData] = useState<any>(null);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [connectedAt, setConnectedAt] = useState<string | null>(null);
    // Get current user for member-level integration
    const { data: user }: any = useCurrentUser({});
    // New hooks for sync configuration management
    const { data: googleCalendarIntegration }: any = useGoogleCalendarIntegration({
        workspaceId: workspace?.id,
        userId: user?.user_id,
        enabled: !!workspace?.id && !!user?.user_id
    });

    // Get the integration ID for AI Memory functionality
    const integrationId = googleCalendarIntegration?.[0]?.id;

    return (
        <MemberLevelIntegrationProvider>
            <GoogleCalendarPageContent
                isConnected={isConnected}
                connectedAt={connectedAt}
                integrationLoading={integrationLoading}
                integrationId={integrationId}
                onConnect={() => { }}
                connectionData={connectionData}
                setConnectionData={setConnectionData}
                setIsConnected={setIsConnected}
                setConnectedAt={setConnectedAt}
                setIntegrationLoading={setIntegrationLoading}
            />
        </MemberLevelIntegrationProvider>
    );
};

export default GoogleCalendarPage;
